// src/pages/MySchedulePage.js
import { useAuth } from "../context/AuthContext";
import { useEffect, useMemo, useRef, useState } from "react";
import StaffLayout from "../components/StaffLayout"; // ✅ staff sidebar layout
import { departmentIconMap } from "../utils/departmentIcons";
import { API_BASE } from "../config";

import * as FaIcons from "react-icons/fa";
import * as MdIcons from "react-icons/md";
import * as FiIcons from "react-icons/fi";
import * as GiIcons from "react-icons/gi";

// ✅ Fallback icons if backend does not send icon_name
const FALLBACK_ICON_BY_LOC = {
  CONTROL_ROOM: "FiMonitor",
  GATEKEEPER: "FiShield",
  THAW: "FiCloud",
  FLOAT: "FiShuffle",
};

function locKeyFromRaw(locRaw) {
  const loc = String(locRaw || "").toUpperCase();
  if (!loc) return "OTHER";
  if (loc.includes("CONTROL")) return "CONTROL_ROOM";
  if (loc.includes("GATE")) return "GATEKEEPER";
  if (loc.includes("THAW")) return "THAW";
  if (loc.includes("FLOAT") || loc.includes("FLOATER")) return "FLOAT";
  return "OTHER";
}

function iconFromName(iconName) {
  if (!iconName) return null;

  const name = String(iconName).trim();
  const packs = [FaIcons, MdIcons, FiIcons, GiIcons];

  for (const pack of packs) {
    const Comp = pack[name];
    if (Comp) return <Comp size={14} />;
  }

  return null;
}

/* ---------- date helpers ---------- */
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function safeDate(dt) {
  if (!dt) return null;

  // ✅ Fix timezone shift for "YYYY-MM-DD"
  if (typeof dt === "string") {
    const m = dt.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const d = Number(m[3]);
      return new Date(y, mo, d); // local midnight ✅
    }
  }

  const d = new Date(dt);
  return Number.isNaN(d.getTime()) ? null : d;
}

function asEventWhen(e) {
  return (
    e?.start ||
    e?.datetime ||
    e?.date ||
    e?.start_time ||
    e?.when ||
    e?.starts_at ||
    e?.startAt ||
    e?.start_at
  );
}
function eventTitle(e) {
  return e?.title || e?.name || e?.summary || "Event";
}

function prettyMonthDay(d) {
  if (!d) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
}
function mkRange(start, end) {
  if (!start || !end) return "—";
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

  const left = prettyMonthDay(start);
  // ✅ if same month, show just day number on right, but only if different day
  if (sameMonth) {
    const rightDay = String(end.getDate()).padStart(2, "0");
    const leftDay = String(start.getDate()).padStart(2, "0");
    return leftDay === rightDay ? left : `${left} – ${rightDay}`;
  }
  return `${left} – ${prettyMonthDay(end)}`;
}
function getWeekSunToSat(baseDate = new Date()) {
  const d = new Date(baseDate);
  const day = d.getDay(); // 0 Sun
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}
function looksLikeRealDateString(v) {
  // Accept ISO (2026-01-25), RFC strings, or anything Date() can parse well,
  // but reject tiny strings like "Sun", "Mon", etc.
  if (v == null) return false;
  const s = String(v).trim();
  if (s.length < 6) return false;
  // common bad values: "Sun" "Mon"
  if (/^(sun|mon|tue|wed|thu|fri|sat)$/i.test(s)) return false;
  return true;
}
function extractEntryDate(entry) {
  // supports many possible backend key names
  return (
    entry?.date ||
    entry?.shift_date ||
    entry?.duty_date ||
    entry?.day_date ||
    entry?.work_date ||
    entry?.start_date ||
    entry?.week_date ||
    null
  );
}

async function tryFetchWeekRange(weekId, authFetch) {
  const candidates = [
    `/api/weeks/${weekId}`,
    `/api/week/${weekId}`,
    `/api/week?weekId=${weekId}`,
    `/api/weeks?weekId=${weekId}`,
  ];

  for (const url of candidates) {
    try {
      const res = await authFetch(url);
      if (!res.ok) continue;

      const data = await res.json().catch(() => null);

      if (!data) continue;

      const w = data.week || data;
      const start = safeDate(w.start_date || w.startDate || w.from || w.start);
      const end = safeDate(w.end_date || w.endDate || w.to || w.end);

      if (start && end) return { start, end };
    } catch {
      // ignore and try next
    }
  }
  return null;
}

function attendanceToNoteLabel(status) {
  const s = String(status || "").toUpperCase();

  if (!s) return "";
  if (s.includes("NO_CALL_NO_SHOW") || s === "NCNS") return "NCNS";
  if (s.includes("SICK")) return "SICK";
  if (s.includes("LATE")) return "LATE";
  if (s.includes("ABSENT")) return "ABSENT";
  return s; // fallback (whatever backend sends)
}

// ✅ detect leave/exception (same keywords as you already use)
function isLeaveEntry(entry) {
  const dt = String(entry?.dutyType || entry?.duty_type || "").toUpperCase();

  // ✅ IMPORTANT: SHIFT and ATTENDANCE are NEVER leave
  if (dt === "SHIFT" || dt === "ATTENDANCE") {
    return false;
  }

  const t = `${entry?.subDuty || ""} ${entry?.note || ""} ${entry?.dutyType || ""}`.toLowerCase();

  return (
    t.includes("day off") ||
    t.includes("vacation") ||
    t.includes("holiday lieu") ||
    t.includes("holiday") ||
    t.includes("unpaid personal business") ||
    t.includes("personal business") ||
    t.includes("leave of absence") ||
    t.includes("loa") ||
    t.includes("short term disability") ||
    t.includes("std") ||
    t.includes("sick") ||
    t.includes("ncns") ||
    t.includes("no show") ||
    t.includes("no call") ||
    t.includes("floater") ||
    t.includes("floter")
  );
}

// ✅ "04:00 – 10:00" => minutes
function minutesFromTimeRange(range) {
  if (!range) return 0;

  const parts = String(range).split("–").map((s) => s.trim());
  if (parts.length !== 2) return 0;

  const [startStr, endStr] = parts;

  const toMin = (hhmm) => {
    const m = String(hhmm).match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const h = Number(m[1]);
    const mm = Number(m[2]);
    if (Number.isNaN(h) || Number.isNaN(mm)) return null;
    return h * 60 + mm;
  };

  const s = toMin(startStr);
  const e = toMin(endStr);
  if (s == null || e == null) return 0;

  // handle overnight shifts
  let diff = e - s;
  if (diff < 0) diff += 24 * 60;

  return diff;
}

// ✅ "Thu SICK" => "SICK"
function cleanLeaveLabel(label, day) {
  const s = String(label || "").trim();
  if (!s) return "";
  if (!day) return s;
  return s.replace(new RegExp(`^${day}\\s+`, "i"), "").trim();
}

function sanitizeNote(entry) {
  const dt = String(entry?.dutyType || "").toUpperCase();
  const note = String(entry?.note || "").trim();

  if (!note) return "";

  // ✅ If it's SHIFT, hide leave words inside NOTE
  if (dt === "SHIFT") {
    const t = note.toLowerCase();
    if (
      t.includes("vacation") ||
      t.includes("day off") ||
      t.includes("holiday") ||
      t.includes("loa") ||
      t.includes("leave of absence") ||
      t.includes("std") ||
      t.includes("sick") ||
      t.includes("ncns")
    ) {
      return ""; // ✅ hide wrong leave notes on shift rows
    }
  }

  return note;
}

function MySchedulePage() {
  const { user, token, authFetch } = useAuth();

  const userId = user?.id || 2; // demo fallback
  const [weekId, setWeekId] = useState(null);

  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [todayEventTitle, setTodayEventTitle] = useState("N/A");

  // ✅ week range text
  const [weekRangeText, setWeekRangeText] = useState("—");

  const [filter, setFilter] = useState("ALL");

  const [deptInfo, setDeptInfo] = useState(null);
  const [deptThemes, setDeptThemes] = useState({ byKey: {}, list: [] });

  const [announcement, setAnnouncement] = useState(null);
  const [announcementLoading, setAnnouncementLoading] = useState(false);

  // ✅ slider state (UI only)
  const cardsRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // ✅ helpers (needed by table + cards)
  const getDutyLabel = (entry) => {
    if (!entry) return "—";

    // prefer leave/notes text if it looks like a leave type
    const t = `${entry?.subDuty || ""} ${entry?.note || ""} ${entry?.dutyType || ""}`.toLowerCase();

    if (entry?.dutyType === "DAY_OFF" || t.includes("day off")) return "DAY OFF";
    if (t.includes("vacation")) return "VACATION";
    if (t.includes("holiday lieu")) return "HOLIDAY LIEU";
    if (t.includes("holiday")) return "HOLIDAY";
    if (t.includes("unpaid personal business")) return "UNPAID PB";
    if (t.includes("personal business")) return "PERSONAL BUSINESS";
    if (t.includes("leave of absence") || t.includes("loa")) return "LOA";
    if (t.includes("short term disability") || t.includes("std")) return "STD";
    if (t.includes("sick")) return "SICK";
    if (t.includes("ncns") || t.includes("no call") || t.includes("no show")) return "NCNS";
    if (t.includes("floater") || t.includes("floter")) return "FLOATER";

    // normal shift fallback
    if (String(entry?.dutyType || "").toUpperCase() === "SHIFT") return "SHIFT";
    return String(entry?.dutyType || "—").toUpperCase();
  };

  const getDutyDisplayText = (entry) => {
    if (!entry) return "—";

    const dt = String(entry?.dutyType || entry?.duty_type || "").toUpperCase();

    // ✅ ATTENDANCE: show LATE / SICK / NCNS
    if (dt === "ATTENDANCE") {
      const v =
        entry?.dutyLocation ??
        entry?.duty_location ??
        entry?.attendance_status ??
        entry?.status ??
        "ATTENDANCE";
      return String(v).toUpperCase();
    }

    // ✅ Leave types should show leave label
    if (isLeaveEntry(entry) || dt === "DAY_OFF") {
      return getDutyLabel(entry);
    }

    // ✅ SHIFT should show location name (Input / Float / etc)
    if (dt === "SHIFT") {
      const loc = getLocFullName(entry) || entry?.dutyLocation || "";
      return loc ? String(loc).toUpperCase() : "SHIFT";
    }

    return getDutyLabel(entry);
  };

  const getSubDuty = (entry) => {
    const v = entry?.subDuty || entry?.sub_duty || entry?.subDutyName || "";
    return v ? String(v) : "N/A";
  };

  const fmtBreak = (entry) => {
    if (!entry) return "N/A";

    const b =
      entry.breakMinutes ??
      entry.break_minutes ??
      entry.breakTime ??
      entry.break_time ??
      entry.break ??
      "";

    if (b !== "" && b != null) {
      if (typeof b === "number") return `${b} min`;

      const asNum = Number(b);
      if (!Number.isNaN(asNum) && String(b).trim() !== "") return `${asNum} min`;

      return String(b);
    }

    const bs = entry.break_start || entry.breakStart || "";
    const be = entry.break_end || entry.breakEnd || "";

    if (bs && be) return `${bs} – ${be}`;

    return "N/A";
  };

  const getRowClass = (entry, isToday) => {
    const classes = ["my-row"];
    if (isToday) classes.push("my-row--today");

    const dt = String(entry?.dutyType || entry?.duty_type || "").toUpperCase();

    // ✅ IMPORTANT: SHIFT / ATTENDANCE should NEVER get leave row classes from note text
    if (dt === "SHIFT" || dt === "ATTENDANCE") {
      // location row tint for SHIFT
      const k = getLocKey(entry);
      if (k === "CONTROL_ROOM") classes.push("my-row--control");
      if (k === "GATEKEEPER") classes.push("my-row--gatekeeper");
      if (k === "THAW") classes.push("my-row--thaw");
      if (k === "FLOAT") classes.push("my-row--float");
      return classes.join(" ");
    }

    // ✅ For non-shift rows (leave types), use text keywords
    const t = `${entry?.subDuty || ""} ${entry?.note || ""} ${entry?.dutyType || ""}`.toLowerCase();

    if (entry?.dutyType === "DAY_OFF" || t.includes("day off")) classes.push("my-row--dayoff");
    else if (t.includes("vacation")) classes.push("my-row--vacation");
    else if (t.includes("holiday")) classes.push("my-row--holiday");
    else if (t.includes("unpaid personal business")) classes.push("my-row--upb");
    else if (t.includes("personal business")) classes.push("my-row--pb");
    else if (t.includes("leave of absence") || t.includes("loa")) classes.push("my-row--loa");
    else if (t.includes("std")) classes.push("my-row--std");
    else if (t.includes("sick")) classes.push("my-row--sick");
    else if (t.includes("ncns") || t.includes("no call") || t.includes("no show")) classes.push("my-row--noshow");
    else if (t.includes("floater") || t.includes("floter")) classes.push("my-row--floater");

    return classes.join(" ");
  };

  useEffect(() => {
    if (!token) return;

    (async () => {
      try {
        const r = await authFetch("/api/weeks/current");
        const w = await r.json().catch(() => null);
        if (w?.id) setWeekId(Number(w.id));
      } catch (e) {
        console.error(e);
      }
    })();
  }, [token, authFetch]);

  // ✅ Load department info to get Manager name + themes (with safe fallbacks)
  useEffect(() => {
    if (!token || !user?.department_id) return;

    const normKeyLocal = (s) =>
      String(s || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();

    (async () => {
      try {
        // 1) Try to fetch THIS department only (most likely to exist)
        let dept = null;
        try {
          const r1 = await authFetch(`/api/departments/${user.department_id}?_=${Date.now()}`, {
            cache: "no-store",
          });
          if (r1.ok) dept = await r1.json().catch(() => null);
        } catch {}

        // normalize if backend wraps it
        dept = dept?.department || dept?.row || dept || null;
        setDeptInfo(dept);
        console.log("DEPT:", dept);

        // 2) OPTIONAL: Try to fetch department list for themes (don’t break if missing)
        const tryUrls = [
          "/api/departments?themes=1",
          "/api/departments",
          "/api/departments/list",
          "/api/departments/all",
        ];

        let rows = [];
        for (const url of tryUrls) {
          try {
            const sep = url.includes("?") ? "&" : "?";
            const r = await authFetch(`${url}${sep}_=${Date.now()}`, { cache: "no-store" });
            if (!r.ok) continue;

            const j = await r.json().catch(() => null);
            rows = Array.isArray(j)
              ? j
              : Array.isArray(j?.rows)
              ? j.rows
              : Array.isArray(j?.departments)
              ? j.departments
              : [];

            if (rows.length) break;
          } catch {}
        }

        const byKey = {};
        const cleanStr = (v) => {
          const s = String(v ?? "").trim();
          return s ? s : null;
        };

        for (const d of rows) {
          const key = normKeyLocal(d?.name);
          if (!key) continue;
          byKey[key] = {
            id: d.id,
            name: d.name,
            color: cleanStr(d.color) || cleanStr(d.department_color) || null,
            icon: cleanStr(d.icon) || null,
          };
        }

        setDeptThemes({ byKey, list: rows });
      } catch {
        setDeptInfo(null);
        setDeptThemes({ byKey: {}, list: [] });
      }
    })();
  }, [token, user?.department_id, authFetch]);

  // ✅ Load department announcement
  useEffect(() => {
    if (!token || !user?.department_id) return;

    let alive = true;

    (async () => {
      try {
        setAnnouncementLoading(true);

        const res = await authFetch(
          `/api/department-announcement?departmentId=${Number(user.department_id)}&_=${Date.now()}`,
          { cache: "no-store" }
        );

        const data = await res.json().catch(() => null);
        console.log("ANNOUNCEMENT DATA:", data);
        console.log("USER DEPARTMENT ID:", user?.department_id);

        if (!alive) return;

        if (!res.ok) {
          setAnnouncement(null);
          return;
        }

        setAnnouncement(data || null);
      } catch (e) {
        console.error("announcement load error:", e);
        if (alive) setAnnouncement(null);
      } finally {
        if (alive) setAnnouncementLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [token, user?.department_id, authFetch]);

  // ---------- LOAD DATA FROM BACKEND ----------
  useEffect(() => {
    if (!token) return;
    if (!weekId) {
      setLoading(false);
      return;
    }

    let alive = true;

    async function fetchSchedule() {
      try {
        setLoading(true);
        setError("");

        // ✅ Week range: try backend first (best)
        const backendWeek = await tryFetchWeekRange(weekId, authFetch);
        if (backendWeek?.start && backendWeek?.end) {
          if (alive) setWeekRangeText(mkRange(backendWeek.start, backendWeek.end));
        } else {
          if (alive) setWeekRangeText("—");
        }

        const res = await authFetch(`/api/my-schedule?userId=${userId}&weekId=${weekId}`);

        const data = await res.json().catch(() => []);
        if (!res.ok) throw new Error(data?.error || "Failed to load schedule");
        console.log("RAW my-schedule rows:", data);

        const base = [
          { dayIndex: 0, day: "Sun" },
          { dayIndex: 1, day: "Mon" },
          { dayIndex: 2, day: "Tue" },
          { dayIndex: 3, day: "Wed" },
          { dayIndex: 4, day: "Thu" },
          { dayIndex: 5, day: "Fri" },
          { dayIndex: 6, day: "Sat" },
        ];

        const filled = base.map((d) => {
          const entry = data.find((e) => e.day_of_week === d.dayIndex);
          const loc = entry ? entry.duty_location || entry.dutyLocation || "" : "";
          const attendanceStatus = String(entry?.attendance_status || "").toUpperCase();
          const attendanceNote = String(entry?.attendance_note || "").trim();
          const hasAttendance = Boolean(attendanceStatus);

          if (entry) {
            const loc = entry.duty_location || entry.dutyLocation || "";
            console.log("DAY", d.day, "duty_type:", entry.duty_type, "loc:", loc);
          }

          console.log("ICON FROM BACKEND:", entry?.icon_name, entry?.iconName);

          if (!entry) {
            return {
              id: null,
              ...d,
              dutyType: "OTHER",
              time: "",
              note: "",
              subDuty: "",
              breakMinutes: "",
              entryDate: null,
              dutyLocation: "",
              iconName: "",
            };
          }

          // ✅ FIX: const must be OUTSIDE the return object
          const locKey = locKeyFromRaw(entry.duty_location || entry.dutyLocation || "");

          return {
            id: entry.id,
            ...d,

            dutyType: hasAttendance ? "ATTENDANCE" : entry.duty_type,

            time:
              hasAttendance
                ? ""
                : entry.start_time && entry.end_time
                ? `${entry.start_time} – ${entry.end_time}`
                : "",

            note: hasAttendance ? attendanceNote || attendanceStatus : entry.note || "",

            subDuty: hasAttendance
              ? ""
              : entry.sub_duty || entry.subDuty || entry.sub_duty_name || entry.subDutyName || "",

            breakMinutes: hasAttendance
              ? ""
              : entry.break_minutes ??
                entry.breakMinutes ??
                entry.break_time ??
                entry.breakTime ??
                entry.break ??
                (entry.break_start && entry.break_end
                  ? `${entry.break_start} – ${entry.break_end}`
                  : ""),

            breakStart: hasAttendance ? "" : entry.break_start || entry.breakStart || "",
            breakEnd: hasAttendance ? "" : entry.break_end || entry.breakEnd || "",

            dutyLocation: hasAttendance
              ? attendanceStatus
              : loc ||
                entry.duty_location_name ||
                entry.dutyLocationName ||
                entry.location ||
                entry.work_location ||
                entry.workLocation ||
                entry.site ||
                entry.area ||
                "",

            iconName:
              entry?.icon_name ||
              entry?.iconName ||
              entry?.department_icon ||
              entry?.dept_icon ||
              entry?.duty_icon ||
              FALLBACK_ICON_BY_LOC[locKey] ||
              "",

            attendance_status: entry.attendance_status || "",
            attendance_note: entry.attendance_note || "",

            entryDate: extractEntryDate(entry),
          };
        });

        // ✅ default table data
        if (alive) setWeeklySchedule(filled);
        console.log("FILLED MON:", filled.find((r) => r.dayIndex === 1));

        // ✅ If week range not from backend, infer (only if reliable)
        if (!(backendWeek?.start && backendWeek?.end)) {
          const dates = filled
            .map((x) => (looksLikeRealDateString(x.entryDate) ? safeDate(x.entryDate) : null))
            .filter(Boolean);

          const uniq = Array.from(new Set(dates.map((d) => toISODate(d)))).sort();

          if (uniq.length >= 2) {
            const sorted = dates.sort((a, b) => a.getTime() - b.getTime());
            if (alive) setWeekRangeText(mkRange(sorted[0], sorted[sorted.length - 1]));
          } else {
            const { start, end } = getWeekSunToSat(new Date());
            if (alive) setWeekRangeText(mkRange(start, end));
          }
        }
      } catch (err) {
        console.error(err);
        if (alive) setError(err.message || "Error loading schedule");
      } finally {
        if (alive) setLoading(false);
      }
    }

    fetchSchedule();

    return () => {
      alive = false;
    };
  }, [userId, weekId, token]);

  // ---------- LOAD TODAY EVENT (3rd card) ----------
  useEffect(() => {
    let alive = true;

    async function fetchTodayEvent() {
      try {
        const now = new Date();
        const from = toISODate(now);
        const to = toISODate(addDays(now, 1));

        const res = await authFetch(`/api/events?from=${from}&to=${to}`);

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          if (!alive) return;
          setTodayEventTitle("N/A");
          return;
        }

        const list = Array.isArray(data) ? data : Array.isArray(data?.events) ? data.events : [];

        const todayISO = from;
        const todayEvents = list
          .map((e) => ({ e, d: safeDate(asEventWhen(e)) }))
          .filter(({ d }) => d && toISODate(d) === todayISO)
          .sort((a, b) => a.d.getTime() - b.d.getTime())
          .map(({ e }) => e);

        if (!alive) return;
        setTodayEventTitle(todayEvents.length ? eventTitle(todayEvents[0]) : "N/A");
      } catch {
        if (!alive) return;
        setTodayEventTitle("N/A");
      }
    }

    fetchTodayEvent();
    return () => {
      alive = false;
    };
  }, [token]);

  function normKey(s) {
    return String(s || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, " ")
      .trim();
  }

  function hexToRgba(hex, a = 0.18) {
    const h = String(hex || "").trim().replace("#", "");
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  function hexToRgb(hex) {
    if (!hex) return null;
    const h = String(hex || "").trim().replace("#", "");
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;

    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }

  function darkenHex(hex, amount = 0.35) {
    const rgb = hexToRgb(hex);
    if (!rgb) return "#0f172a";

    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
    const factor = 1 - amount;

    return `rgb(${clamp(rgb.r * factor)}, ${clamp(rgb.g * factor)}, ${clamp(rgb.b * factor)})`;
  }

  function renderDeptIcon(iconKey, size = 14, color = "#0f172a") {
    const key = String(iconKey || "").trim();
    const Comp = departmentIconMap?.[key];
    if (!Comp) return null;
    return <Comp size={size} color={color} style={{ color }} />;
  }

  function getThemeForEntry(entry) {
    if (!entry) return null;
    const loc = normKey(getLocFullName(entry) || entry.dutyLocation || "");
    if (!loc) return null;

    if (deptThemes.byKey?.[loc]) return deptThemes.byKey[loc];

    for (const d of deptThemes.list || []) {
      const dk = normKey(d?.name);
      if (dk && loc.includes(dk)) return d;
    }
    return null;
  }

  /* ================== LOCATION META + HELPERS (MUST BE ABOVE getDutyChipStyle) ================== */

  const LOCATION_META = {
    CONTROL_ROOM: {
      match: ["CONTROL ROOM", "CONTROL", "CR"],
      full: "Control Room",
      chip: { backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" },
      badge: {
        backgroundColor: "rgba(59,130,246,.26)",
        border: "1px solid rgba(147,197,253,.65)",
        color: "#eff6ff",
      },
      icon: "🏢",
    },
    GATEKEEPER: {
      match: ["GATEKEEPER", "GATE KEEPER", "GK"],
      full: "Gatekeeper",
      chip: { backgroundColor: "#ede9fe", color: "#5b21b6", border: "1px solid #c4b5fd" },
      badge: {
        backgroundColor: "rgba(139,92,246,.24)",
        border: "1px solid rgba(196,181,253,.65)",
        color: "#f5f3ff",
      },
      icon: "🛂",
    },
    THAW: {
      match: ["THAW"],
      full: "Thaw",
      chip: { backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" },
      badge: {
        backgroundColor: "rgba(245,158,11,.24)",
        border: "1px solid rgba(252,211,77,.65)",
        color: "#fffbeb",
      },
      icon: "❄️",
    },
    FLOAT: {
      match: ["FLOAT", "FLOATER"],
      full: "Float",
      chip: { backgroundColor: "#e2e8f0", color: "#334155", border: "1px solid #cbd5e1" },
      badge: {
        backgroundColor: "rgba(100,116,139,.22)",
        border: "1px solid rgba(203,213,225,.60)",
        color: "#f8fafc",
      },
      icon: "🧩",
    },
  };

  const getLocKey = (entry) => {
    const locRaw = entry?.dutyLocation || entry?.location || entry?.work_location || "";

    const loc = String(locRaw).toUpperCase();
    if (!loc) return "OTHER";

    for (const key of Object.keys(LOCATION_META)) {
      const meta = LOCATION_META[key];
      if (meta.match.some((m) => loc.includes(String(m).toUpperCase()))) return key;
    }
    return "OTHER";
  };

  const getLocFullName = (entry) => {
    const key = getLocKey(entry);
    if (key && key !== "OTHER" && LOCATION_META[key]) return LOCATION_META[key].full;
    const loc = entry?.dutyLocation ? String(entry.dutyLocation) : "";
    return loc || "";
  };

  const getLocIcon2 = (entry) => {
    const key = getLocKey(entry);
    if (key && key !== "OTHER" && LOCATION_META[key]) return LOCATION_META[key].icon;
    return "📍";
  };

  function attendanceBadgeIcon(v) {
    const s = String(v || "").toUpperCase();
    if (s.includes("LATE")) return "⏰";
    if (s.includes("SICK")) return "🤒";
    if (s.includes("NCNS") || s.includes("NO")) return "🚫";
    return "⚠️";
  }

  const getDutyChipStyle = (entry) => {
    if (!entry) {
      return { backgroundColor: "#f8fafc", color: "#0f172a" };
    }

    const text = `${entry?.subDuty || ""} ${entry?.note || ""} ${getDutyLabel(entry) || ""}`.toLowerCase();

    // ✅ attendance highlight
    if (String(entry?.dutyType || "").toUpperCase() === "ATTENDANCE") {
      const v = String(entry?.dutyLocation || "").toUpperCase();
      if (v.includes("LATE")) return { backgroundColor: "#fde68a", color: "#92400e" }; // yellow
      if (v.includes("SICK")) return { backgroundColor: "#fecaca", color: "#7f1d1d" }; // red
      if (v.includes("NCNS") || v.includes("NO")) return { backgroundColor: "#bbf7d0", color: "#14532d" }; // green
      return { backgroundColor: "#e5e7eb", color: "#111827" };
    }

    // -------- LEAVE TYPES FIRST --------
    if (entry.dutyType === "DAY_OFF" || text.includes("day off")) {
      return { backgroundColor: "#baffc9", color: "#14532d" };
    }

    if (text.includes("vacation")) {
      return { backgroundColor: "#bae1ff", color: "#1e40af" };
    }

    if (text.includes("holiday")) {
      return { backgroundColor: "#ffffba", color: "#854d0e" };
    }

    if (text.includes("personal business") && !text.includes("unpaid")) {
      return { backgroundColor: "#d7baff", color: "#5b21b6" };
    }

    if (text.includes("unpaid personal business")) {
      return { backgroundColor: "#ffdfba", color: "#92400e" };
    }

    if (text.includes("leave of absence") || text.includes("loa")) {
      return { backgroundColor: "#bafff2", color: "#0f766e" };
    }

    if (text.includes("std")) {
      return { backgroundColor: "#ffb3ba", color: "#7f1d1d" };
    }

    if (text.includes("sick")) {
      return { backgroundColor: "#ffc6ba", color: "#7c2d12" };
    }

    if (text.includes("no show") || text.includes("ncns")) {
      return { backgroundColor: "#e6ffba", color: "#365314" };
    }

    // -------- NORMAL SHIFT / LOCATIONS --------
    if (entry.dutyType === "SHIFT") {
      const theme = getThemeForEntry(entry);
      const bg1 = hexToRgba(theme?.color, 0.22);
      const bg2 = hexToRgba(theme?.color, 0.10);
      const textColor = darkenHex(theme?.color, 0.35);

      if (theme?.color && bg1 && bg2) {
        return {
          backgroundImage: `linear-gradient(135deg, ${bg1} 0%, ${bg2} 55%, ${bg1} 100%)`,
          backgroundSize: "200% 200%",
          backgroundPosition: "0% 50%",
          color: textColor,
          border: `1px solid ${hexToRgba(theme.color, 0.35) || "rgba(2,6,23,.10)"}`,
        };
      }

      // ✅ 2) fallback to your old hardcoded location styles
      const key = getLocKey(entry);
      if (key && key !== "OTHER" && LOCATION_META[key]) return LOCATION_META[key].chip;

      // ✅ 3) last fallback
      const loc = entry?.dutyLocation ? String(entry.dutyLocation).toUpperCase() : "";
      if (loc) {
        return {
          backgroundColor: "#eef2ff",
          color: "#3730a3",
          border: "1px solid #c7d2fe",
        };
      }
    }

    // -------- FINAL FALLBACK --------
    return { backgroundColor: "#e5e7eb", color: "#111827" };
  };

  const getSubDutyChipStyle = (entry) => {
    const dt = String(entry?.dutyType || "").toUpperCase();

    // Attendance rows
    if (dt === "ATTENDANCE") {
      const v = String(entry?.dutyLocation || "").toUpperCase();

      if (v.includes("LATE")) {
        return {
          backgroundColor: "#fde68a",
          color: "#92400e",
          border: "1px solid rgba(146,64,14,.18)",
        };
      }
      if (v.includes("SICK")) {
        return {
          backgroundColor: "#fecaca",
          color: "#7f1d1d",
          border: "1px solid rgba(127,29,29,.18)",
        };
      }
      if (v.includes("NCNS") || v.includes("NO")) {
        return {
          backgroundColor: "#bbf7d0",
          color: "#14532d",
          border: "1px solid rgba(20,83,45,.18)",
        };
      }

      return {
        backgroundColor: "#e5e7eb",
        color: "#111827",
        border: "1px solid rgba(17,24,39,.10)",
      };
    }

    // Shift rows → match location/department theme
    if (dt === "SHIFT") {
      const theme = getThemeForEntry(entry);
      const bg = hexToRgba(theme?.color, 0.16);

      if (theme?.color && bg) {
        return {
          backgroundColor: bg,
          color: darkenHex(theme.color, 0.35),
          border: `1px solid ${hexToRgba(theme.color, 0.28) || "rgba(2,6,23,.10)"}`,
        };
      }

      const key = getLocKey(entry);
      if (key && key !== "OTHER" && LOCATION_META[key]) {
        return LOCATION_META[key].chip;
      }
    }

    // Leave rows → match duty chip color
    return getDutyChipStyle(entry);
  };

  const getTimeChipStyle = (entry) => {
    const dt = String(entry?.dutyType || "").toUpperCase();

    // SHIFT rows → match department/location theme
    if (dt === "SHIFT") {
      const theme = getThemeForEntry(entry);
      const bg = hexToRgba(theme?.color, 0.16);

      if (theme?.color && bg) {
        return {
          backgroundColor: bg,
          color: darkenHex(theme.color, 0.35),
          border: `1px solid ${hexToRgba(theme.color, 0.28) || "rgba(2,6,23,.10)"}`,
        };
      }

      const key = getLocKey(entry);
      if (key && key !== "OTHER" && LOCATION_META[key]) {
        return LOCATION_META[key].chip;
      }
    }

    // ATTENDANCE / LEAVE / fallback
    return getSubDutyChipStyle(entry);
  };

  const getLocBadgeStyle = (entry) => {
    const duty = String(entry?.dutyType || "").toUpperCase();

    // ✅ ATTENDANCE badge colors
    if (duty === "ATTENDANCE") {
      const v = String(entry?.dutyLocation || "").toUpperCase();

      if (v.includes("LATE")) {
        return {
          backgroundColor: "rgba(253,230,138,.35)",
          border: "1px solid rgba(146,64,14,.30)",
          color: "#fffbeb",
        };
      }
      if (v.includes("SICK")) {
        return {
          backgroundColor: "rgba(254,202,202,.35)",
          border: "1px solid rgba(127,29,29,.30)",
          color: "#fff1f2",
        };
      }
      if (v.includes("NCNS") || v.includes("NO")) {
        return {
          backgroundColor: "rgba(187,247,208,.35)",
          border: "1px solid rgba(20,83,45,.28)",
          color: "#ecfdf5",
        };
      }

      return {
        backgroundColor: "rgba(229,231,235,.35)",
        border: "1px solid rgba(17,24,39,.18)",
        color: "#f8fafc",
      };
    }

    // ✅ SHIFT badge colors
    if (duty === "SHIFT") {
      // ✅ 1) department theme first
      const theme = getThemeForEntry(entry);
      const bg = hexToRgba(theme?.color, 0.22);

      if (theme?.color && bg) {
        return {
          backgroundColor: bg,
          border: `1px solid ${hexToRgba(theme.color, 0.35) || "rgba(2,6,23,.12)"}`,
          color: "#0f172a", // readable on light background
        };
      }

      // ✅ 2) fallback to your old location meta
      const key = getLocKey(entry);
      if (key && key !== "OTHER" && LOCATION_META[key]) return LOCATION_META[key].badge;

      // ✅ 3) last fallback
      const loc = entry?.dutyLocation ? String(entry.dutyLocation).toUpperCase() : "";
      if (loc) {
        return {
          backgroundColor: "rgba(238,242,255,.30)",
          border: "1px solid rgba(199,210,254,.60)",
          color: "#eef2ff",
        };
      }
    }

    // ✅ default
    return {
      backgroundColor: "rgba(0,0,0,.12)",
      border: "1px solid rgba(255,255,255,.18)",
      color: "#fff",
    };
  };

  // ✅ counts
  const totalWorkingDays = weeklySchedule.filter((d) => d.dutyType === "SHIFT").length;

  // ✅ leave days count (SICK/VACATION/HOLIDAY/etc.)
  const leaveDays = weeklySchedule.filter((d) => isLeaveEntry(d) || d.dutyType === "DAY_OFF").length;

  // ✅ total hours (only normal SHIFT, not leave)
  const totalMinutes = weeklySchedule.reduce((sum, d) => {
    if (d?.dutyType !== "SHIFT") return sum;
    if (isLeaveEntry(d)) return sum; // ✅ don't count leave SHIFT
    return sum + minutesFromTimeRange(d.time);
  }, 0);

  const totalHours = totalMinutes / 60;
  const totalHoursText = Number.isInteger(totalHours) ? `${totalHours} hrs` : `${totalHours.toFixed(1)} hrs`;

  const todayISO = useMemo(() => toISODate(new Date()), []);
  const todayIndex = new Date().getDay();
  const todayDuty = weeklySchedule[todayIndex];

  const filteredSchedule = useMemo(() => {
    const list = weeklySchedule || [];

    if (filter === "ALL") return list;
    if (filter === "SHIFT") return list.filter((e) => e.dutyType === "SHIFT" && !isLeaveEntry(e));

    if (filter === "LEAVE") return list.filter((e) => isLeaveEntry(e) || e.dutyType === "DAY_OFF");

    // location filters
    return list.filter((e) => {
      if (e.dutyType !== "SHIFT") return false;
      if (isLeaveEntry(e)) return false;
      return getLocKey(e) === filter;
    });
  }, [weeklySchedule, filter]);

  const visibleSchedule = useMemo(() => {
    // Sunday = show full list again
    if (todayIndex === 0) return filteredSchedule;

    // Hide passed days only in table view
    return filteredSchedule.filter((entry) => entry.dayIndex >= todayIndex);
  }, [filteredSchedule, todayIndex]);

  const tomorrowIndex = (todayIndex + 1) % 7;
  const tomorrowDuty = weeklySchedule[tomorrowIndex];

  const todaySummary = (() => {
    if (!todayDuty) return "Today: —";

    const leave = isLeaveEntry(todayDuty) || todayDuty.dutyType === "DAY_OFF";
    if (leave) {
      const label = cleanLeaveLabel(getDutyLabel(todayDuty), todayDuty.day);
      return `Today: ${label || "OFF"}`;
    }

    const loc =
      String(todayDuty?.dutyType || "").toUpperCase() === "ATTENDANCE"
        ? String(todayDuty?.dutyLocation || "").toUpperCase()
        : getLocFullName(todayDuty) || getDutyLabel(todayDuty);

    const time = todayDuty.time || "—";
    return `Today: ${loc} • ${time}`;
  })();

  const tomorrowSummary = (() => {
    if (!tomorrowDuty) return "Tomorrow: —";

    const leave = isLeaveEntry(tomorrowDuty) || tomorrowDuty.dutyType === "DAY_OFF";
    if (leave) {
      const label = cleanLeaveLabel(getDutyLabel(tomorrowDuty), tomorrowDuty.day);
      return `Tomorrow: ${label || "OFF"}`;
    }

    const loc =
      String(tomorrowDuty?.dutyType || "").toUpperCase() === "ATTENDANCE"
        ? String(tomorrowDuty?.dutyLocation || "").toUpperCase()
        : getLocFullName(tomorrowDuty) || getDutyLabel(tomorrowDuty);

    const time = tomorrowDuty.time || "—";
    return `Tomorrow: ${loc} • ${time}`;
  })();

  const isTodaySick =
    (getDutyLabel(todayDuty) || "").toUpperCase().includes("SICK") ||
    (todayDuty?.note || "").toUpperCase().includes("SICK");

  const barTheme = isTodaySick
    ? {
        bg: "linear-gradient(135deg, #fff1f2, #ffe4e6)", // light red/pink
        border: "1px solid rgba(244,63,94,.20)",
        todayColor: "#be123c", // strong red
        tomorrowColor: "#0f766e", // green
      }
    : {
        bg: "linear-gradient(135deg, #eff6ff, #e0f2fe)", // light blue
        border: "1px solid rgba(59,130,246,.16)",
        todayColor: "#1e40af", // blue
        tomorrowColor: "#0f766e", // green
      };

  // ✅ Profile fallbacks
  const profileName = user?.fullName || user?.name || user?.displayName || user?.username || "Team member";
  const profileEmail = user?.email || user?.mail || user?.username || "—";
  const profileRole = user?.role || user?.userRole || user?.type || "Staff";
  const roleKey = String(profileRole || "Staff").toLowerCase();

  const roleClass =
    roleKey.includes("admin") ? "jp-badge--admin" : roleKey.includes("manager") ? "jp-badge--manager" : "jp-badge--staff";

  const profileDept = user?.department || user?.dept || user?.team || "FedEx Operations";
  const profileLocation = user?.location || user?.site || "YYZR AM Sort";
  const profileManager =
    deptInfo?.manager_name ||
    deptInfo?.managerName ||
    deptInfo?.manager ||
    user?.manager_name ||
    user?.managerName ||
    user?.manager ||
    user?.supervisor_name ||
    user?.supervisorName ||
    user?.supervisor ||
    "—";

  // ✅ Job position + icon (ADD HERE)
  const jobPosRaw =
    user?.job_position || user?.jobPosition || user?.position || user?.job_title || user?.jobTitle || "";

  const jobKey = String(jobPosRaw).toLowerCase();

  const jobIcon = jobKey.includes("manager")
    ? "🧭"
    : jobKey.includes("admin")
    ? "🛡️"
    : jobKey.includes("forklift")
    ? "🏗️"
    : jobKey.includes("cargo")
    ? "📦"
    : jobKey.includes("handler")
    ? "📦"
    : jobKey.includes("scanner")
    ? "🔫"
    : jobKey.includes("loader")
    ? "🚚"
    : jobKey.includes("control room")
    ? "🏢"
    : jobKey.includes("gatekeeper")
    ? "🛂"
    : jobKey.includes("thaw")
    ? "❄️"
    : "💼";

  const initials = String(profileName || "T")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

  const firstName =
    String(profileName || "")
      .trim()
      .split(/\s+/)[0] || "Team";

  // ✅ slider helpers (UI only)
  const totalSlides = 3;
  const activeDotClass =
  activeSlide === 0
    ? "dot-duty"
    : activeSlide === 1
    ? "dot-week"
    : "dot-event";

const updateActiveSlideFromScroll = () => {
  const el = cardsRef.current;
  if (!el) return;

  const maxScroll = el.scrollWidth - el.clientWidth;

  if (maxScroll <= 0) {
    setActiveSlide(0);
    return;
  }

  const progress = el.scrollLeft / maxScroll; // 0 to 1
  const nextIndex = Math.round(progress * (totalSlides - 1));
  const safeIndex = Math.max(0, Math.min(totalSlides - 1, nextIndex));

  setActiveSlide((prev) => (prev === safeIndex ? prev : safeIndex));
};

useEffect(() => {
  const timer = setTimeout(() => {
    updateActiveSlideFromScroll();
  }, 100);

  const handleResize = () => {
    updateActiveSlideFromScroll();
  };

  window.addEventListener("resize", handleResize);

  return () => {
    clearTimeout(timer);
    window.removeEventListener("resize", handleResize);
  };
}, []);

  // ---------- UI ----------
  if (loading) {
    return (
      <StaffLayout>
        <div className="my-schedule-page">
          <p>Loading your schedule...</p>
        </div>
      </StaffLayout>
    );
  }

  if (error) {
    return (
      <StaffLayout>
        <div className="my-schedule-page">
          <p style={{ color: "red" }}>Error: {error}</p>
        </div>
      </StaffLayout>
    );
  }

  const dutyMain =
    todayDuty?.dutyType === "DAY_OFF"
      ? "Day off"
      : String(todayDuty?.dutyType || "").toUpperCase() === "ATTENDANCE"
      ? String(todayDuty?.dutyLocation || "ATTENDANCE").toUpperCase()
      : String(todayDuty?.dutyType || "").toUpperCase() === "SHIFT"
      ? todayDuty?.time
        ? todayDuty.time
        : getLocFullName(todayDuty) || String(todayDuty?.dutyLocation || "SHIFT").toUpperCase()
      : "No shift";

  const dutySub =
    todayDuty?.dutyType === "DAY_OFF"
      ? "Enjoy your day off."
      : String(todayDuty?.dutyType || "").toUpperCase() === "ATTENDANCE"
      ? "Attendance marked by admin."
      : String(todayDuty?.dutyType || "").toUpperCase() === "SHIFT"
      ? todayDuty?.note
        ? todayDuty.note
        : todayDuty?.time
        ? "Regular shift."
        : "Time not set by admin."
      : "Regular shift.";

  // Texts like LinkedIn
  const jobPosition = user?.job_position || user?.jobPosition || user?.position || user?.job_title || user?.jobTitle || "";

  const headlineText =
    user?.headline || (jobPosition ? `${profileDept} | ${jobPosition}` : `${profileDept} | Automation & Operational Analytics`);

  const city = user?.city || "Brampton";
  const province = user?.province || "Ontario";
  const country = user?.country || "Canada";
  const pronouns = user?.pronouns || "";
  const companyName = user?.company || "FedEx Canada";
  const connections = user?.connections || "500+";

  const announcementHtmlRaw = String(announcement?.message_html || "").trim();

  const announcementHtml =
    announcementHtmlRaw === "<div><br></div>" ||
    announcementHtmlRaw === "<p><br></p>" ||
    announcementHtmlRaw === "<br>" ||
    announcementHtmlRaw === "&nbsp;"
      ? ""
      : announcementHtmlRaw;

  const showAnnouncement = Boolean(announcement);

  const announcementUpdatedAt = announcement?.updated_at ? safeDate(announcement.updated_at) : null;

  const announcementUpdatedText = announcementUpdatedAt ? announcementUpdatedAt.toLocaleString() : "—";

  const announcementUpdatedBy = announcement?.updated_by_name || announcement?.created_by_name || "Management";

  return (
    <StaffLayout>
      <div className="my-schedule-page">
        <style>{`
        /* ================== GLOBAL (MORE PROFESSIONAL) ================== */
          .my-schedule-page{
            font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI",
              Roboto, Helvetica, Arial, sans-serif;

            background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 45%, #f8fafc 100%);
            min-height: 100vh;
            padding: 14px; /* ✅ less outer padding */
          }

          /* ================== PROFILE HEADER ================== */
          .li-wrap{
            position: relative;
            isolation: isolate;
            background:
              linear-gradient(180deg, #fbfcff 0%, #f3f6fd 100%);
            border: 1px solid rgba(2,6,23,.08);
            border-radius: 16px;
            overflow:hidden;
            box-shadow: 0 12px 24px rgba(2,6,23,.07);
            margin: 0 auto 12px;
            max-width: 1040px;
          }

          .li-wrap::before{
            content:"";
            position:absolute;
            inset:0;
            pointer-events:none;
            background:
              radial-gradient(circle at 20% 25%, rgba(59,130,246,.05) 0, rgba(59,130,246,0) 28%),
              radial-gradient(circle at 82% 18%, rgba(139,92,246,.05) 0, rgba(139,92,246,0) 24%),
              repeating-linear-gradient(
                135deg,
                rgba(15,23,42,.018) 0px,
                rgba(15,23,42,.018) 1px,
                transparent 1px,
                transparent 16px
              );
            opacity: .65;
            z-index: 0;
          }

          .li-wrap::after{
            content:"";
            position:absolute;
            inset:auto -80px -90px auto;
            width: 260px;
            height: 260px;
            border-radius: 999px;
            background: radial-gradient(circle, rgba(255,255,255,.80), rgba(255,255,255,0) 70%);
            pointer-events:none;
            z-index: 0;
          }

          .li-body{
            position: relative;
            z-index: 1;
            padding: 14px 16px 14px;
          }

          .li-signature{
            position:absolute;
            left: 50%;
            top: 34px;
            transform: translateX(-8%);
            pointer-events:none;
            user-select:none;
            white-space: nowrap;
            font-family: "Self Deception Regular", "Segoe Script", "Brush Script MT", cursive;
            font-size: 96px;
            line-height: 1;
            font-weight: 400;
            color: rgba(59,130,246,.10);
            letter-spacing: 1px;
            z-index: 0;
            filter: blur(.15px);
          }

          /* ✅ Avatar: a bit smaller */
          .li-avatar{
            width: 112px;     /* ✅ reduced */
            height: 112px;    /* ✅ reduced */
            border-radius: 999px;
            background: #0b1220;
            color:#fff;
            display:flex;
            align-items:center;
            justify-content:center;
            font-weight: 950;
            font-size: 30px;  /* ✅ reduced */
            letter-spacing: .7px;
            border: 5px solid #fff;
            box-shadow: 0 14px 26px rgba(2,6,23,.18);
            margin-top: 0px;
            margin-bottom: 10px;
            user-select:none;
          }

          .li-grid{
            display:grid;
            grid-template-columns: 1fr 300px; /* ✅ slightly tighter right */
            gap: 14px;
            margin-top: 10px;
            align-items:center;
          }
          @media (max-width: 980px){
            .li-grid{ grid-template-columns: 1fr; }
          }

          .li-name-row{
            display:flex;
            align-items:center;
            gap: 10px;
            flex-wrap: wrap;
            margin-top: 6px;
            margin-top: 0;
            position: relative;
            z-index: 1;
          }

          /* ✅ Name: calmer / more corporate */
          .li-name{
            font-size: 24px;     /* ✅ smaller than before */
            font-weight: 900;    /* ✅ less aggressive */
            letter-spacing: -0.4px;
            color:#0f172a;
            line-height: 1.15;
          }

          .li-pronouns{
            font-size: 13px;
            font-weight: 800;
            color: rgba(15,23,42,.60);
          }

          .li-verified{
            width: 20px;
            height: 20px;
            border-radius: 999px;
            background: rgba(59,130,246,.12);
            border: 1px solid rgba(59,130,246,.22);
            color: #1d4ed8;
            display:inline-flex;
            align-items:center;
            justify-content:center;
            font-size: 13px;
            font-weight: 950;
          }

          .li-headline{
            margin-top: 6px;
            font-size: 13px;
            font-weight: 800;
            color: rgba(15,23,42,.78);
            position: relative;
            z-index: 1;
          }

          .li-meta{
            margin-top: 8px;
            display:flex;
            gap: 10px;
            flex-wrap: wrap;
            font-size: 12px;
            font-weight: 800;
            color: rgba(15,23,42,.68);
          }
          .li-sep{
            color: rgba(15,23,42,.22);
            font-weight: 900;
          }

          /* ✅ Week/Role chips: less “pill”, more “enterprise” */
          .my-schedule-chips{
            display:flex;
            gap: 10px;
            justify-content:flex-start;
            flex-wrap: wrap;
            margin-top: 10px;
            position: relative;
            z-index: 1;
          }
          .filter-chip{
            display:inline-flex;
            align-items:center;
            gap: 8px;
            padding: 9px 12px;
            border-radius: 14px; /* ✅ less round */
            font-weight: 900;
            font-size: 12px;
            border: 1px solid rgba(2,6,23,.10);
            background: #fff;
            color: #0f172a;
            box-shadow: 0 8px 16px rgba(2,6,23,.06); /* ✅ softer */
          }
          .filter-chip span{ font-weight: 900; }

          .filter-chip--week{
            border: 1px solid rgba(59,130,246,.16);
            background: linear-gradient(135deg, rgba(59,130,246,.12), rgba(147,197,253,.14));
            color: #1d4ed8;
          }
          .filter-chip--user{
            border: 1px solid rgba(139,92,246,.14);
            background: linear-gradient(135deg, rgba(139,92,246,.10), rgba(196,181,253,.14));
            color: #5b21b6;
          }

          /* ✅ Right side tighter + cleaner */
          .li-right{
            display:flex;
            flex-direction: column;
            gap: 10px;       /* ✅ tighter */
            padding-top: 52px;
            position: relative;
            z-index: 1;
          }
          .li-mini{
            display:flex;
            align-items:center;
            gap: 10px;
          }
          .li-mini-badge{
            width: 40px;   /* ✅ slightly smaller */
            height: 40px;
            border-radius: 12px;
            background: #f1f5f9;
            border: 1px solid rgba(2,6,23,.10);
            display:flex;
            align-items:center;
            justify-content:center;
            font-weight: 950;
            color:#0f172a;
          }
          .li-mini-title{
            font-weight: 900;
            font-size: 13px;
            color:#0f172a;
            line-height: 1.15;
          }
          .li-mini-sub{
            margin-top: 2px;
            font-weight: 800;
            font-size: 12px;
            color: rgba(15,23,42,.62);
          }

          /* ================== JOB POSITION BADGE (KEEP LOGIC) ================== */
          .jp-badge{
            display:inline-flex;
            align-items:center;
            gap:8px;
            padding:7px 12px;
            border-radius: 14px; /* ✅ less round */
            font-weight: 850;
            font-size: 13px;
            letter-spacing:0.2px;
            border:1px solid rgba(0,0,0,0.08);
            color:#0f172a;
            background: rgba(15,23,42,0.05);
            transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
            will-change: transform;
          }
          .jp-badge::before{
            content: var(--jp-icon, "💼");
            font-size:14px;
          }
          .jp-badge:hover{
            transform: translateY(-1px);
            box-shadow: 0 10px 18px rgba(2,6,23,.12);
            filter: saturate(1.03);
          }
          .jp-badge--admin{
            background: linear-gradient(135deg, rgba(239,68,68,0.14), rgba(248,113,113,0.14));
            border: 1px solid rgba(239,68,68,0.18);
          }
          .jp-badge--manager{
            background: linear-gradient(135deg, rgba(16,185,129,0.14), rgba(52,211,153,0.14));
            border: 1px solid rgba(16,185,129,0.18);
          }
          .jp-badge--staff{
            background: linear-gradient(135deg, rgba(79,70,229,0.14), rgba(168,85,247,0.14));
            border: 1px solid rgba(79,70,229,0.14);
          }

            .ms-cards-wrap{
              max-width: 1040px;
              margin: 10px auto 10px;
              position: relative;
            }

            .ms-cards{
              display: flex;
              gap: 14px;
              overflow-x: auto;
              overflow-y: hidden;
              scroll-snap-type: x mandatory;
              -webkit-overflow-scrolling: touch;
              padding: 4px 2px 10px;
              scrollbar-width: none;
              scroll-behavior: smooth;
            }

            .ms-cards::-webkit-scrollbar{
              display: none;
            }

            .ms-cards > .ms-card{
              flex: 0 0 min(86%, 360px);
              scroll-snap-align: center;
              scroll-snap-stop: always;
              transition:
                transform .28s ease,
                opacity .28s ease,
                filter .28s ease,
                box-shadow .28s ease;
            }

            .ms-slider-dots{
              display:flex;
              justify-content:center;
              align-items:center;
              gap:8px;
              margin-top: 10px;
            }

            .ms-slider-dot{
              width: 8px;
              height: 8px;
              border-radius: 999px;
              background: rgba(148,163,184,.45);
              transition: all .22s ease;
            }

            .ms-slider-dot.active{
              width: 10px;
              height: 10px;
            }

            .ms-slider-dot.active.dot-duty{
              background: #22c55e;
              box-shadow: 0 0 0 4px rgba(34,197,94,.14);
            }

            .ms-slider-dot.active.dot-week{
              background: #3b82f6;
              box-shadow: 0 0 0 4px rgba(59,130,246,.14);
            }

            .ms-slider-dot.active.dot-event{
              background: #8b5cf6;
              box-shadow: 0 0 0 4px rgba(139,92,246,.14);
            }

            @media (min-width: 981px){
              .ms-cards{
                overflow: visible;
                gap: 16px;
                padding: 2px 2px 8px;
              }

              .ms-cards > .ms-card{
                flex: 0 0 calc((100% - 32px) / 3);
                transform: none !important;
                opacity: 1 !important;
                filter: none !important;
              }

              .ms-slider-dots{
                display: none;
              }
            }

            @media (max-width: 768px){
                .ms-cards{
                  gap: 12px;
                  padding: 4px 12px 8px;
                }

              .ms-cards > .ms-card{
                flex: 0 0 90%;
                min-height: 170px;
                opacity: .72;
                transform: scale(.94);
                filter: saturate(.9);
              }

              .ms-cards > .ms-card.is-active{
                opacity: 1;
                transform: scale(1);
                filter: saturate(1.02);
                box-shadow: 0 18px 30px rgba(2,6,23,.14);
              }

              .ms-cards > .ms-card.is-left,
              .ms-cards > .ms-card.is-right{
                opacity: .86;
                transform: scale(.97);
              }

              .ms-slider-dots{
                margin-top: 8px;
              }
            }

          /* ✅ Smaller, less tall, consistent */
          .ms-card{
            position: relative;
            width: 100%;
            height: 198px;                 /* ✅ reduced height */
            border-radius: 18px;           /* ✅ slightly less round */
            overflow: hidden;
            border: 1px solid rgba(2,6,23,.08);
            box-shadow: 0 12px 24px rgba(2,6,23,.10);
            padding: 14px;
            color: rgba(255,255,255,.98);
          }

          /* ✅ Reduce decorative noise (more professional) */
          .ms-card::before{
            content:"";
            position:absolute;
            inset:0;
            background: radial-gradient(900px 260px at 18% 18%, rgba(255,255,255,.30), rgba(255,255,255,0) 60%);
            pointer-events:none;
            opacity: .38; /* ✅ lower */
          }

          .ms-card .ms-dots-left,
          .ms-card .ms-dots-right{
            position:absolute;
            pointer-events:none;
            background-image: radial-gradient(circle, rgba(255,255,255,.95) 1.2px, transparent 1.3px);
            background-size: 10px 10px;
            opacity: .12; /* ✅ lower */
          }
          .ms-card .ms-dots-left{
            left: 14px; top: 48px;
            width: 68px; height: 74px;
            border-radius: 14px;
          }
          .ms-card .ms-dots-right{
            right: 14px; top: 12px;
            width: 60px; height: 60px;
            border-radius: 16px;
          }

          .ms-card .ms-watermark{
            position:absolute;
            right: 12px;
            top: 34px;
            font-weight: 900;
            font-size: 40px; /* ✅ smaller */
            letter-spacing: 1px;
            opacity: .09;    /* ✅ lower */
            color: rgba(255,255,255,.95);
            user-select:none;
            pointer-events:none;
            line-height: 1;
          }

          .ms-card .ms-blob{
            position:absolute;
            right: -75px;
            bottom: -95px;
            width: 250px;
            height: 250px;
            border-radius: 80px;
            transform: rotate(12deg);
            opacity: .14; /* ✅ lower */
            background: rgba(255,255,255,.95);
            pointer-events:none;
          }

          .ms-card-inner{
            position: relative;
            z-index: 2;
            height: 100%;
            display:flex;
            flex-direction:column;
          }

          /* ✅ Titles consistent */
          .ms-big{
            font-weight: 900;
            font-size: 18px;
            letter-spacing: -0.3px;
            line-height: 1.15;
            margin-top: 2px;
            margin-bottom: 6px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .ms-small{
            font-weight: 750;
            font-size: 12px;
            opacity: .92;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .ms-bottom{
            margin-top: auto;
            display:flex;
            justify-content: space-between;
            gap: 12px;
            align-items:flex-end;
            padding-top: 10px;
          }

          /* ✅ Meta hierarchy: label smaller, value normal */
          .ms-meta{
            display:flex;
            flex-direction:column;
            gap: 7px;
            font-size: 12px;
            font-weight: 800;
            opacity: .95;
            flex: 1 1 auto;
            min-width: 0;
          }
          .ms-meta-row{
            display:flex;
            gap: 8px;
            align-items: baseline;
            white-space: nowrap;
            min-width: 0;
          }
          .ms-meta-label{
            font-weight: 750;
            font-size: 10px;
            letter-spacing: .45px;
            opacity: .82;
            text-transform: uppercase;
          }
          .ms-meta-value{
            font-weight: 700;
            font-size: 12px;
            opacity: .96;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1 1 auto;
            min-width: 0;
          }

          /* ✅ Badge: smaller + less pill */
          .ms-badge{
            flex: 0 0 auto;
            padding: 8px 10px;
            border-radius: 12px; /* ✅ less pill */
            background: rgba(0,0,0,.12);
            border: 1px solid rgba(255,255,255,.18);
            font-weight: 950;
            font-size: 10px;
            letter-spacing: .6px;
            text-transform: uppercase;
            backdrop-filter: blur(6px);
            max-width: 120px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          /* ✅ Slightly calmer gradients */
          .ms-card--duty{
            background: linear-gradient(135deg, #16a34a 0%, #22c55e 50%, #86efac 100%);
          }
          .ms-card--week{
            background: linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #93c5fd 100%);
          }
          .ms-card--event{
            background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #c4b5fd 100%);
          }

          /* ================== TODAY/TOMORROW BAR (TIGHTER) ================== */
          .tt-bar{
            max-width: 1040px;
            margin: 10px auto 12px;
            padding: 10px 14px;  /* ✅ smaller */
            border-radius: 14px;
            box-shadow: 0 10px 18px rgba(2,6,23,.06);
            font-weight: 900;
            display: flex;
            justify-content: space-between;
            gap: 12px;
            flex-wrap: wrap;
            transition: transform .12s ease, box-shadow .12s ease;
          }
          .tt-bar:hover{
            transform: translateY(-1px);
            box-shadow: 0 16px 30px rgba(2,6,23,.10);
          }
          .tt-item{
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
          }

          /* ================== ANNOUNCEMENT ================== */
          .ann-wrap{
            max-width: 1040px;
            margin: 0 auto 12px;
          }

          .ann-card{
            background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
            border: 1px solid rgba(2,6,23,.08);
            border-radius: 18px;
            box-shadow: 0 12px 24px rgba(2,6,23,.07);
            overflow: hidden;
          }

          .ann-head{
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 14px 16px 10px;
            border-bottom: 1px solid rgba(2,6,23,.06);
            background: linear-gradient(135deg, rgba(59,130,246,.08), rgba(139,92,246,.07));
          }

          .ann-title{
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-weight: 950;
            font-size: 15px;
            color: #0f172a;
            letter-spacing: -.2px;
          }

          .ann-meta{
            font-size: 12px;
            font-weight: 700;
            color: rgba(15,23,42,.62);
            text-align: right;
          }

          .ann-body{
            padding: 14px 16px 16px;
            color: #0f172a;
            font-size: 14px;
            line-height: 1.6;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          .ann-body p{
            margin: 0 0 10px;
          }

          .ann-body p:last-child{
            margin-bottom: 0;
          }

          .ann-body strong,
          .ann-body b{
            font-weight: 900;
          }

          .ann-body em,
          .ann-body i{
            font-style: italic;
          }

          .ann-body u{
            text-decoration: underline;
          }

          .ann-body span{
            max-width: 100%;
          }

          /* ================== TABLE UI (UNCHANGED LOGIC) ================== */
          .my-table-card{
            border-radius: 18px;
            overflow: hidden;
            max-width: 1140px;
            width: 100%;
            margin: 0 auto;
          }

          .my-table-header{
            padding-bottom: 10px;
          }
          .my-table-scroll{
            overflow-x: auto;
            border-radius: 16px;
            -webkit-overflow-scrolling: touch;
          }
          .my-table-scroll::-webkit-scrollbar{ height: 10px; }
          .my-table-scroll::-webkit-scrollbar-thumb{
            background: rgba(2,6,23,.15);
            border-radius: 999px;
          }

          .my-schedule-table{
            width: 100%;
            min-width: 860px;
            table-layout: fixed;
            border-collapse: separate;
            border-spacing: 0;
            background: #fff;
            border: 1px solid rgba(2,6,23,.08);
            border-radius: 16px;
            overflow: hidden;
          }

          .my-schedule-table thead th{
            position: sticky;
            top: 0;
            z-index: 2;
            background: #f8fafc;
            color: #0f172a;
            font-weight: 900;
            font-size: 12px;
            letter-spacing: .3px;
            text-transform: uppercase;
            padding: 12px 12px;
            border-bottom: 1px solid rgba(2,6,23,.10);
            white-space: nowrap;
          }

          .my-schedule-table tbody td{
            padding: 12px 12px;
            border-bottom: 1px solid rgba(2,6,23,.06);
            font-size: 13px;
            color: #0f172a;
            vertical-align: middle;
            white-space: nowrap;
          }

          /* ✅ NOTE wraps */
          .my-schedule-table td.col-note,
          .my-schedule-table th.col-note{
            white-space: normal !important;
            word-break: break-word;
            overflow-wrap: anywhere;
            line-height: 1.35;
          }
          .my-schedule-table td:not(.col-note){
            white-space: nowrap;
          }

          .my-schedule-table tbody tr:hover td{ background: transparent; }
          .my-schedule-table tbody tr:not(.my-row--dayoff):not(.my-row--vacation):not(.my-row--holiday):not(.my-row--pb):not(.my-row--upb):not(.my-row--loa):not(.my-row--std):not(.my-row--sick):not(.my-row--noshow):not(.my-row--floater):hover td{
            background:
              linear-gradient(135deg,
                var(--shiftRowHoverBg2, transparent),
                var(--shiftRowHoverBg, #f8fafc)
              );
          }

          .my-day-pill{
            display:inline-flex;
            align-items:center;
            gap: 8px;
            font-weight: 950;
          }
          .my-dot{
            width: 8px;
            height: 8px;
            border-radius: 999px;
            background: rgba(2,6,23,.25);
          }

          .my-chip{
            display:inline-flex;
            align-items:center;
            padding: 6px 10px;
            border-radius: 999px;
            border: 1px solid rgba(2,6,23,.10);
            background: #f8fafc;
            font-weight: 850;
            font-size: 12px;
            color: rgba(15,23,42,.86);
            max-width: 240%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .my-duty-chip{
            display:inline-flex;
            align-items:center;
            padding: 6px 10px;
            border-radius: 999px;
            border: 1px solid rgba(2,6,23,.10);
            font-weight: 950;
            font-size: 12px;
            max-width: 240px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .my-duty-chip > span{
            display: inline-flex;
            align-items: center;
            gap: 6px;
            min-width: 0;
          }

          .my-duty-chip > span > span:last-child{
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .my-time{
            font-variant-numeric: tabular-nums;
            font-weight: 850;
            color: rgba(15,23,42,.88);
          }

          .my-time-chip{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            min-width: 88px;
            max-width: 100%;
            font-variant-numeric: tabular-nums;
            font-weight: 900;
          }

          .my-row td{
            transition:
              background-color .25s ease,
              background-image .25s ease,
              box-shadow .25s ease,
              border-color .25s ease;
          }

          .my-row td{
            transition:
              background-color .25s ease,
              background-image .25s ease,
              box-shadow .25s ease,
              border-color .25s ease;

            background:
              linear-gradient(
                135deg,
                var(--shiftRowBg2, transparent),
                var(--shiftRowBg, transparent)
              );
          }

          .my-row:hover td{
            background:
              linear-gradient(
                135deg,
                var(--shiftRowHoverBg2, transparent),
                var(--shiftRowHoverBg, #f8fafc)
              );
          }

          /* ✅ left accent line (uses theme color when available) */
          .my-row td:first-child{
            box-shadow: inset 4px 0 0 var(--shiftRowAccent, transparent);
          }

          /* TODAY highlight */
          .my-row--today td{
            box-shadow: inset 3px 0 0 rgba(37,99,235,.55);
          }

          .my-row--today:not(.my-row--dayoff):not(.my-row--vacation):not(.my-row--holiday):not(.my-row--pb):not(.my-row--upb):not(.my-row--loa):not(.my-row--std):not(.my-row--sick):not(.my-row--noshow):not(.my-row--floater) td{
            background: rgba(37,99,235,.06);
          }

          /* ✅ leave row colors kept */
          .my-row--dayoff td{ background: rgba(186,255,201,.35); }
          .my-row--dayoff:hover td{ background: rgba(186,255,201,.50); }

          .my-row--vacation td{ background: rgba(186,225,255,.35); }
          .my-row--vacation:hover td{ background: rgba(186,225,255,.50); }

          .my-row--holiday td{ background: rgba(255,255,186,.40); }
          .my-row--holiday:hover td{ background: rgba(255,255,186,.55); }

          .my-row--pb td{ background: rgba(215,186,255,.35); }
          .my-row--pb:hover td{ background: rgba(215,186,255,.50); }

          .my-row--upb td{ background: rgba(255,223,186,.40); }
          .my-row--upb:hover td{ background: rgba(255,223,186,.55); }

          .my-row--loa td{ background: rgba(186,255,242,.35); }
          .my-row--loa:hover td{ background: rgba(186,255,242,.50); }

          .my-row--std td{ background: rgba(255,179,186,.38); }
          .my-row--std:hover td{ background: rgba(255,179,186,.52); }

          .my-row--sick td{ background: rgba(255,198,186,.40); }
          .my-row--sick:hover td{ background: rgba(255,198,186,.55); }

          .my-row--noshow td{ background: rgba(230,255,186,.40); }
          .my-row--noshow:hover td{ background: rgba(230,255,186,.55); }

          /* ✅ Fixed column widths (DESKTOP ONLY) */
          .my-schedule-table th.col-day,
          .my-schedule-table td.col-day { width: 80px; }

          .my-schedule-table th.col-duty,
          .my-schedule-table td.col-duty { width: 170px; }

          .my-schedule-table th.col-sub,
          .my-schedule-table td.col-sub { width: 190px; }

          .my-schedule-table th.col-time,
          .my-schedule-table td.col-time { width: 130px; }

          .my-schedule-table th.col-break,
          .my-schedule-table td.col-break { width: 130px; }

          /* ✅ Desktop note column should not become too wide */
          .my-schedule-table th.col-note,
          .my-schedule-table td.col-note { width: 220px; }

          /* ✅ Sticky Day column (so it’s always visible) */
          .my-schedule-table thead th.col-day{
            position: sticky;
            left: 0;
            z-index: 5;
            background: #f8fafc;
          }

          .my-schedule-table tbody td.col-day{
            position: sticky;
            left: 0;
            z-index: 4;
            background: #fff;
            border-right: 1px solid rgba(2,6,23,.06);
          }

          /* ================== MOBILE CARD VIEW ================== */
            .my-mobile-cards{
              display: none;
              max-width: 1140px;
              margin: 0 auto;
            }

            .my-mobile-card{
              background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
              border: 1px solid rgba(2,6,23,.08);
              border-radius: 18px;
              box-shadow:
                0 10px 22px rgba(2,6,23,.06),
                0 1px 0 rgba(255,255,255,.8) inset;
              padding: 12px;
              margin-bottom: 14px;
            }

            .my-mobile-card-head{
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              margin-bottom: 10px;
            }

            .my-mobile-day{
              display: inline-flex;
              align-items: center;
              gap: 8px;
              font-weight: 950;
              font-size: 14px;
              color: #0f172a;
            }

            .my-mobile-day-dot{
              width: 8px;
              height: 8px;
              border-radius: 999px;
              background: rgba(2,6,23,.25);
            }

            .my-mobile-today{
              display: inline-flex;
              align-items: center;
              justify-content: center;
              padding: 5px 9px;
              border-radius: 999px;
              background: rgba(37,99,235,.10);
              border: 1px solid rgba(37,99,235,.18);
              color: #1d4ed8;
              font-size: 10px;
              font-weight: 950;
              letter-spacing: .4px;
              text-transform: uppercase;
            }

            .my-mobile-grid{
              display: grid;
              grid-template-columns: 1fr;
              gap: 8px;
            }

            .my-mobile-row{
              display: flex;
              flex-direction: column;
              gap: 8px;
              padding: 10px 12px;
              border-radius: 14px;
              background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
              border: 1px solid rgba(2,6,23,.06);
              box-shadow: inset 0 1px 0 rgba(255,255,255,.7);
            }

            .my-mobile-label{
              font-size: 12px;
              font-weight: 900;
              letter-spacing: .5px;
              text-transform: uppercase;
              color: rgba(15,23,42,.60);
            }

            .my-mobile-value{
              min-width: 0;
            }

            .my-mobile-note{
              font-size: 12px;
              line-height: 1.45;
              color: #0f172a;
              word-break: break-word;
              overflow-wrap: anywhere;
            }

            /* ================== MOBILE SWITCH ================== */
            @media (max-width: 768px) {
              .my-table-scroll{
                display: none;
              }

              .my-mobile-cards{
                display: block;
              }

              .my-mobile-card{
                padding: 12px;
                border-radius: 16px;
              }

              .my-mobile-day{
                font-size: 15px;
              }

              .my-mobile-row{
                padding: 10px 12px;
              }

              .my-mobile-note{
                font-size: 12px;
              }

              .my-chip,
              .my-duty-chip{
                font-size: 13.5px;
                padding: 8px 12px;
                font-weight: 700;
                max-width: 100%;
              }

              .my-time-chip{
                min-width: 96px;
                font-size: 13.5px;
                padding: 8px 12px;
                font-weight: 700;
              }
            }

          /* ================== MOBILE FIX ================== */
          @media (max-width: 768px) {
            .my-schedule-page{
              padding: 10px 8px 92px;
            }

            .li-wrap,
            .ms-cards-wrap,
            .tt-bar,
            .my-table-card{
              max-width: 100%;
            }

            .li-body{
              padding: 12px;
            }

            .li-signature{
              font-size: 52px;
              top: 18px;
              left: 56%;
              transform: translateX(-18%);
              opacity: .9;
            }

            .li-grid{
              grid-template-columns: 1fr;
              gap: 12px;
            }

            .li-right{
              padding-top: 0;
            }

            .li-avatar{
              width: 72px;
              height: 72px;
              font-size: 22px;
              border-width: 4px;
              margin-bottom: 8px;
            }

            .li-name{
              font-size: 20px;
            }

            .li-name-row{
              gap: 8px;
            }

            .my-schedule-chips{
              gap: 8px;
            }

            .filter-chip{
              padding: 8px 10px;
              font-size: 11px;
              border-radius: 12px;
            }

            .ms-card{
              height: auto;
              min-height: 170px;
              padding: 12px;
              border-radius: 16px;
            }

            .ms-big{
              font-size: 16px;
              white-space: normal;
            }

            .ms-small{
              font-size: 11px;
              white-space: normal;
            }

            .ms-bottom{
              gap: 10px;
              align-items: flex-start;
            }

            .ms-meta{
              gap: 6px;
            }

            .ms-meta-row{
              white-space: normal;
            }

            .ms-meta-value{
              white-space: normal;
            }

            .ms-badge{
              max-width: 100px;
              font-size: 9px;
              padding: 7px 9px;
            }

            .tt-bar{
              flex-direction: column;
              align-items: flex-start;
              padding: 10px 12px;
            }

            .tt-item{
              font-size: 11px;
              line-height: 1.35;
            }

            .ann-wrap{
              margin-bottom: 10px;
            }

            .ann-head{
              padding: 12px 12px 9px;
              flex-direction: column;
              align-items: flex-start;
            }

            .ann-title{
              font-size: 14px;
            }

            .ann-meta{
              font-size: 11px;
              text-align: left;
            }

            .ann-body{
              padding: 12px;
              font-size: 13px;
            }

            .my-table-card{
              overflow: hidden;
              width: 100%;
            }

            .my-table-header h2{
              font-size: 16px;
              margin: 0;
            }

            .my-table-card{
              overflow: hidden;
            }

            .my-table-header h2{
              font-size: 16px;
              margin: 0;
            }

            .my-table-header{
              padding-bottom: 8px;
            }

            .my-table-header > div{
              gap: 6px !important;
            }

            .my-table-header select{
              padding: 8px 10px !important;
              font-size: 11px;
            }

            .my-table-scroll{
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }

            .my-table-scroll{
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }

              .my-schedule-table{
                min-width: 560px;
                width: 100%;
                table-layout: fixed;
              }
            .my-schedule-table thead th,
            .my-schedule-table tbody td{
              padding: 8px 6px;
              font-size: 11px;
            }

            .my-schedule-table th.col-day,
            .my-schedule-table td.col-day{
              width: 54px;
            }

            .my-schedule-table th.col-duty,
            .my-schedule-table td.col-duty{
              width: 138px;
            }

            .my-schedule-table th.col-sub,
            .my-schedule-table td.col-sub{
              width: 88px;
            }

            .my-schedule-table th.col-time,
            .my-schedule-table td.col-time{
              width: 82px;
            }

            .my-schedule-table th.col-break,
            .my-schedule-table td.col-break{
              width: 82px;
            }

            /* hide NOTE only on mobile */
            .my-schedule-table th.col-note,
            .my-schedule-table td.col-note{
              display: none;
            }

            /* ✅ disable sticky day on mobile */
            .my-schedule-table thead th.col-day,
            .my-schedule-table tbody td.col-day{
              position: static;
              left: auto;
              z-index: auto;
              border-right: none;
              box-shadow: none;
            }

            .my-day-pill{
              gap: 6px;
              font-size: 12px;
            }

            .my-dot{
              width: 7px;
              height: 7px;
            }

            .my-chip,
            .my-duty-chip{
              font-size: 9px;
              padding: 3px 5px;
            }

            .my-time{
              font-size: 11px;
            }

            .my-time-chip{
              min-width: 64px;
              font-size: 10px;
              padding: 3px 6px;
            }

            select{
              max-width: 100%;
            }

            .my-table-header > div{
              flex-direction: column;
              align-items: flex-start !important;
            }

            .my-table-header select{
              width: 100%;
              max-width: 220px;
            }

            .my-duty-chip{
              max-width: 116px;
            }

            .my-duty-chip > span{
              display: inline-flex !important;
              align-items: center;
              gap: 4px !important;
              min-width: 0;
            }

            .my-duty-chip svg{
              flex: 0 0 auto;
            }

            .my-duty-chip > span > span:last-child{
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .ms-badge{
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }

        `}</style>

        {/* ✅ Profile header */}
        <div className="li-wrap">
          <div className="li-body">
            <div className="li-signature" aria-hidden="true">
              {firstName}
            </div>

            <div className="li-grid">
              {/* Left */}
              <div>
                <div className="li-avatar" title={profileName}>
                  {initials || "TM"}
                </div>

                <div className="li-name-row">
                  <div className="li-name">{profileName}</div>
                  <span className="li-verified">✓</span>
                  {pronouns ? <span className="li-pronouns">{pronouns}</span> : null}
                </div>

                <div className="li-headline subline">
                  <span className={`jp-badge ${roleClass}`} style={{ ["--jp-icon"]: `"${jobIcon}"` }}>
                    {jobPosRaw || "—"}
                  </span>
                </div>

                {/* ✅ Optional tiny meta (looks pro, not noisy) */}

                <div className="my-schedule-chips">
                  <div className="filter-chip filter-chip--week">
                    Week: <span>{weekRangeText || "—"}</span>
                  </div>
                  <div className="filter-chip filter-chip--user">
                    Role: <span>{profileRole}</span>
                  </div>
                </div>
              </div>

              {/* Right */}
              <div className="li-right">
                <div className="li-mini">
                  <div className="li-mini-badge">MG</div>
                  <div>
                    <div className="li-mini-title">Manager</div>
                    <div className="li-mini-sub">{profileManager}</div>
                  </div>
                </div>

                <div className="li-mini">
                  <div className="li-mini-badge">@</div>
                  <div>
                    <div className="li-mini-title">Email</div>
                    <div className="li-mini-sub">{profileEmail}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Cards + Today/Tomorrow bar */}
        <div className="ms-cards-wrap">
        <div
          className="ms-cards"
          ref={cardsRef}
          onScroll={updateActiveSlideFromScroll}
          onTouchEnd={updateActiveSlideFromScroll}
          onMouseUp={updateActiveSlideFromScroll}
        >
            {/* Today's duty */}
            <div
                className={`ms-card ms-card--duty ${
                  activeSlide === 0 ? "is-active" : activeSlide > 0 ? "is-left" : "is-right"
                }`}
              >
              <div className="ms-dots-left" />
              <div className="ms-dots-right" />
              <div className="ms-watermark">DUTY</div>
              <div className="ms-blob" />

              <div className="ms-card-inner">
                <div className="ms-big">{dutyMain}</div>
                <div className="ms-small">{dutySub}</div>

                <div className="ms-bottom">
                  <div className="ms-meta">
                    <div className="ms-meta-row">
                      <span className="ms-meta-label">Day</span>
                      <span className="ms-meta-value">{todayDuty ? todayDuty.day : "—"}</span>
                    </div>
                    <div className="ms-meta-row">
                      <span className="ms-meta-label">Duty</span>
                      <span className="ms-meta-value">{getDutyDisplayText(todayDuty) || "—"}</span>
                    </div>
                  </div>

                  <div className="ms-badge" style={getLocBadgeStyle(todayDuty)}>
                    {todayDuty?.dutyType === "DAY_OFF"
                      ? "OFF"
                      : String(todayDuty?.dutyType || "").toUpperCase() === "SHIFT" ||
                        String(todayDuty?.dutyType || "").toUpperCase() === "ATTENDANCE"
                      ? (
                        <>
                          <span style={{ marginRight: 6 }}>
                            {String(todayDuty?.dutyType || "").toUpperCase() === "ATTENDANCE"
                              ? attendanceBadgeIcon(todayDuty?.dutyLocation)
                              : (() => {
                                  const theme = getThemeForEntry(todayDuty);
                                  const themedIcon = renderDeptIcon(theme?.icon, 14, theme?.color || "#0f172a");
                                  return themedIcon || iconFromName(todayDuty?.iconName) || getLocIcon2(todayDuty);
                                })()}
                          </span>

                          {String(todayDuty?.dutyType || "").toUpperCase() === "ATTENDANCE"
                            ? String(todayDuty?.dutyLocation || "").toUpperCase()
                            : todayDuty?.dutyLocation
                            ? String(todayDuty.dutyLocation).toUpperCase()
                            : todayDuty?.time
                            ? "SHIFT"
                            : "N/A"}
                        </>
                      )
                      : "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Week summary */}
            <div
                  className={`ms-card ms-card--week ${
                    activeSlide === 1 ? "is-active" : activeSlide > 1 ? "is-left" : "is-right"
                  }`}
                >
              <div className="ms-dots-left" />
              <div className="ms-dots-right" />
              <div className="ms-watermark">WEEK</div>
              <div className="ms-blob" />

              <div className="ms-card-inner">
                <div className="ms-big">{totalWorkingDays} shifts</div>
                <div className="ms-small">
                  {totalHoursText} total • {leaveDays} leave day(s)
                </div>

                <div className="ms-bottom">
                  <div className="ms-meta">
                    <div className="ms-meta-row">
                      <span className="ms-meta-label">Date</span>
                      <span className="ms-meta-value">{weekRangeText || "—"}</span>
                    </div>
                    <div className="ms-meta-row">
                      <span className="ms-meta-label">Location</span>
                      <span className="ms-meta-value">{profileLocation}</span>
                    </div>
                    <div className="ms-meta-row">
                      <span className="ms-meta-label">Hours</span>
                      <span className="ms-meta-value">{totalHoursText}</span>
                    </div>
                    <div className="ms-meta-row">
                      <span className="ms-meta-label">Leave</span>
                      <span className="ms-meta-value">{leaveDays} day(s)</span>
                    </div>
                  </div>

                  <div className="ms-badge">📅 Summary</div>
                </div>
              </div>
            </div>

            {/* Today's event */}
            <div
                  className={`ms-card ms-card--event ${
                    activeSlide === 2 ? "is-active" : activeSlide > 2 ? "is-left" : "is-right"
                  }`}
                >
              <div className="ms-dots-left" />
              <div className="ms-dots-right" />
              <div className="ms-watermark">EVENT</div>
              <div className="ms-blob" />

              <div className="ms-card-inner">
                <div className="ms-big">{todayEventTitle || "N/A"}</div>
                <div className="ms-small">
                  {todayEventTitle && todayEventTitle !== "N/A" ? "Event scheduled for today." : "No event today."}
                </div>

                <div className="ms-bottom">
                  <div className="ms-meta">
                    <div className="ms-meta-row">
                      <span className="ms-meta-label">Date</span>
                      <span className="ms-meta-value">{todayISO}</span>
                    </div>
                    <div className="ms-meta-row">
                      <span className="ms-meta-label">Location</span>
                      <span className="ms-meta-value">{profileLocation}</span>
                    </div>
                  </div>

                  <div className="ms-badge">{todayEventTitle && todayEventTitle !== "N/A" ? "LIVE" : "FREE"}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="ms-slider-dots" aria-hidden="true">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <span
                key={i}
                className={`ms-slider-dot ${
                  activeSlide === i ? `active ${activeDotClass}` : ""
                }`}
              />
            ))}
          </div>

          {/* ✅ Today + Tomorrow bar (UNDER the cards) */}
          <div className="tt-bar" style={{ background: barTheme.bg, border: barTheme.border }}>
            <span className="tt-item" style={{ color: barTheme.todayColor }}>
              <span aria-hidden="true">🟢</span>
              {todaySummary}
            </span>

            <span className="tt-item" style={{ color: barTheme.tomorrowColor }}>
              <span aria-hidden="true">🔵</span>
              {tomorrowSummary}
            </span>
          </div>
        </div>

        {/* ✅ Department Announcement */}
        {(announcementLoading || showAnnouncement) && (
          <div className="ann-wrap">
            <div className="ann-card">
              <div className="ann-head">
                <div className="ann-title">
                  <span aria-hidden="true">📢</span>
                  Department message
                </div>

                {!announcementLoading && showAnnouncement && (
                  <div className="ann-meta">
                    <div>By: {announcementUpdatedBy}</div>
                    <div>Updated: {announcementUpdatedText}</div>
                  </div>
                )}
              </div>
              <div className="ann-body">
                {announcementLoading ? (
                  <div>Loading message...</div>
                ) : announcementHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: announcementHtml }} />
                ) : (
                  <div style={{ color: "#64748b", fontWeight: 700 }}>
                    No department message available.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ✅ Weekly Table */}
        <div className="my-table-card">
          <div className="my-table-header">
            <h2>Weekly view</h2>

            <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 900, color: "rgba(15,23,42,.75)" }}>Filter:</span>

              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(2,6,23,.12)",
                  fontWeight: 900,
                  background: "#fff",
                }}
              >
                <option value="ALL">All</option>
                <option value="SHIFT">Only SHIFT</option>
                <option value="LEAVE">Only Leave</option>
                <option value="CONTROL_ROOM">Control Room</option>
                <option value="GATEKEEPER">Gatekeeper</option>
                <option value="THAW">Thaw</option>
                <option value="FLOAT">Float</option>
              </select>
            </div>
          </div>

          <div className="my-table-scroll">
            <table className="my-schedule-table">
              <thead>
                <tr>
                  <th className="col-day">Day</th>
                  <th className="col-duty">Duty</th>
                  <th className="col-sub">Sub-duty</th>
                  <th className="col-time">Time</th>
                  <th className="col-break">Break</th>
                  <th className="col-note">Note</th>
                </tr>
              </thead>

              <tbody>
                {visibleSchedule.map((entry) => {
                  const isToday = entry.dayIndex === todayIndex;
                  const chipStyle = getDutyChipStyle(entry);

                  return (
                    <tr
                      key={entry.day}
                      className={getRowClass(entry, isToday)}
                      style={(() => {
                        const dt = String(entry?.dutyType || "").toUpperCase();

                        // ✅ only SHIFT rows (not leave)
                        if (dt === "SHIFT" && !isLeaveEntry(entry)) {
                          const theme = getThemeForEntry(entry);

                          const bg = hexToRgba(theme?.color, 0.10); // light row color
                          const bgHover = hexToRgba(theme?.color, 0.16); // hover row color

                          if (bg && bgHover) {
                            const bg2 = hexToRgba(theme?.color, 0.04);
                            const bgHover2 = hexToRgba(theme?.color, 0.08);

                            return {
                              "--shiftRowAccent": theme?.color || "transparent",
                              "--shiftRowBg": bg,
                              "--shiftRowBg2": bg2 || "transparent",
                              "--shiftRowHoverBg": bgHover,
                              "--shiftRowHoverBg2": bgHover2 || "transparent",
                            };
                          }
                        }

                        return {};
                      })()}
                    >
                      <td className="col-day">
                        <span className="my-day-pill">
                          <span className="my-dot" aria-hidden="true" />
                          {entry.day}
                        </span>
                      </td>

                      <td className="col-duty">
                        <span
                          className="my-duty-chip"
                          style={chipStyle}
                          title={(() => {
                            const time = entry?.time || "";

                            // detect leave/exception
                            const t = `${entry?.subDuty || ""} ${entry?.note || ""} ${entry?.dutyType || ""}`.toLowerCase();

                            const isLeave =
                              t.includes("day off") ||
                              t.includes("vacation") ||
                              t.includes("holiday") ||
                              t.includes("holiday lieu") ||
                              t.includes("personal business") ||
                              t.includes("unpaid personal business") ||
                              t.includes("leave of absence") ||
                              t.includes("loa") ||
                              t.includes("std") ||
                              t.includes("short term disability") ||
                              t.includes("sick") ||
                              t.includes("ncns") ||
                              t.includes("no show") ||
                              t.includes("no call") ||
                              t.includes("floater") ||
                              t.includes("floter");

                            // show location ONLY for normal SHIFT (not leave)
                            const loc =
                              String(entry?.dutyType || "").toUpperCase() === "SHIFT" && !isLeave
                                ? getLocFullName(entry) || (entry?.dutyLocation ? String(entry.dutyLocation) : "")
                                : "";

                            const parts = [loc ? `Location: ${loc}` : null, time ? `Time: ${time}` : null].filter(Boolean);

                            return parts.length ? parts.join(" • ") : "—";
                          })()}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {(() => {
                              const dt = String(entry?.dutyType || "").toUpperCase();

                              if (dt === "ATTENDANCE") {
                                return (
                                  <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center" }}>
                                    {attendanceBadgeIcon(entry?.dutyLocation)}
                                  </span>
                                );
                              }

                              if (dt === "SHIFT") {
                                return (
                                  <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center" }}>
                                    {(() => {
                                      const theme = getThemeForEntry(entry);
                                      const themedIcon = renderDeptIcon(theme?.icon, 14, theme?.color || "#0f172a");
                                      return themedIcon || iconFromName(entry?.iconName) || getLocIcon2(entry);
                                    })()}
                                  </span>
                                );
                              }

                              return null;
                            })()}
                            <span>{getDutyDisplayText(entry)}</span>
                          </span>
                        </span>
                      </td>

                      <td className="col-sub">
                        <span
                          className="my-chip"
                          style={getSubDutyChipStyle(entry)}
                          title={getSubDuty(entry)}
                        >
                          {getSubDuty(entry)}
                        </span>
                      </td>

                      <td className="col-time">
                        <span
                          className="my-chip my-time-chip"
                          style={getTimeChipStyle(entry)}
                          title={entry.time || "N/A"}
                        >
                          {entry.time || "N/A"}
                        </span>
                      </td>

                      <td className="col-break">
                        <span
                          className="my-chip my-time-chip"
                          style={getTimeChipStyle(entry)}
                          title={fmtBreak(entry)}
                        >
                          {fmtBreak(entry)}
                        </span>
                      </td>

                      <td className="col-note">{sanitizeNote(entry) || "N/A"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ✅ MOBILE CARDS (NEW) */}
          <div className="my-mobile-cards">
            {visibleSchedule.map((entry) => {
              const isToday = entry.dayIndex === todayIndex;
              const dutyChipStyle = getDutyChipStyle(entry);
              const subDutyChipStyle = getSubDutyChipStyle(entry);
              const timeChipStyle = getTimeChipStyle(entry);

              return (
                <div
                  key={`mobile-${entry.day}`}
                  className="my-mobile-card"
                >
                  <div className="my-mobile-card-head">
                    <div className="my-mobile-day">
                      <span className="my-mobile-day-dot" />
                      {entry.day}
                    </div>

                    {isToday && <span className="my-mobile-today">Today</span>}
                  </div>

                  <div className="my-mobile-grid">
                    {/* Duty */}
                    <div className="my-mobile-row">
                      <div className="my-mobile-label">Duty</div>
                      <div className="my-mobile-value">
                        <span className="my-duty-chip" style={dutyChipStyle}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {(() => {
                              const dt = String(entry?.dutyType || "").toUpperCase();

                              if (dt === "ATTENDANCE") {
                                return (
                                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                                    {attendanceBadgeIcon(entry?.dutyLocation)}
                                  </span>
                                );
                              }

                              if (dt === "SHIFT") {
                                return (
                                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                                    {(() => {
                                      const theme = getThemeForEntry(entry);
                                      const themedIcon = renderDeptIcon(
                                        theme?.icon,
                                        16,
                                        theme?.color || "#0f172a"
                                      );

                                      return themedIcon || iconFromName(entry?.iconName) || getLocIcon2(entry);
                                    })()}
                                  </span>
                                );
                              }

                              return null;
                            })()}

                            <span>{getDutyDisplayText(entry)}</span>
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* Sub-duty */}
                    <div className="my-mobile-row">
                      <div className="my-mobile-label">Sub-duty</div>
                      <div className="my-mobile-value">
                        <span className="my-chip" style={subDutyChipStyle}>
                          {getSubDuty(entry)}
                        </span>
                      </div>
                    </div>

                    {/* Time */}
                    <div className="my-mobile-row">
                      <div className="my-mobile-label">Time</div>
                      <div className="my-mobile-value">
                        <span className="my-chip my-time-chip" style={timeChipStyle}>
                          {entry.time || "N/A"}
                        </span>
                      </div>
                    </div>

                    {/* Break */}
                    <div className="my-mobile-row">
                      <div className="my-mobile-label">Break</div>
                      <div className="my-mobile-value">
                        <span className="my-chip my-time-chip" style={timeChipStyle}>
                          {fmtBreak(entry)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </StaffLayout>
  );
}

export default MySchedulePage;